import LedgerScannerClient, { LedgerScannerCustomToken } from '../clients/LedgerScannerClient'
import { type PlugToken } from '../interfaces/common'
import { logger } from '../logger'
import { Wallet } from '../providers/WalletProvider'
import { TokenBalance } from '../state/tokenBalances'

const asCustomToken = (token: PlugToken): LedgerScannerCustomToken => {
  return {
    id: token.id,
    address: token.address || '',
    standard: token.standard,
    chain: token.chain,
    solana_program_id: token.additionalData?.solana_program_id,
  }
}

export const getTokenBalances = async (
  wallet: Wallet,
  tokens: PlugToken[],
  chains?: number[]
): Promise<TokenBalance[]> => {
  const ledgerScannerBalances = getLedgerScannerBalances(
    wallet,
    tokens.filter((t) => !t.isCustom),
    tokens.filter((t) => t.isCustom),
    chains
  )
  return (await ledgerScannerBalances).filter((tb) => !!tb)
}

const getLedgerScannerBalances = async (
  wallet: Wallet,
  tokens?: PlugToken[],
  customTokens?: PlugToken[],
  chains?: number[]
) => {
  if (!tokens?.length || !customTokens) return []

  try {
    const balances = await LedgerScannerClient.fetchBalances(
      wallet,
      tokens.map((t) => t.id),
      customTokens.map(asCustomToken),
      chains
    )

    return tokens.concat(customTokens).map((token) => ({
      id: token.id,
      balance: balances[token.id] || '0',
    }))
  } catch (e) {
    logger.error(`Failed to get LS token balances`, { originalError: e })

    return []
  }
}

export const getTokenBalance = async (
  wallet: Wallet,
  t: PlugToken,
  chains?: number[]
): Promise<string> => {
  const response = await LedgerScannerClient.fetchBalances(
    wallet,
    t.isCustom ? [] : [t.id],
    t.isCustom ? [asCustomToken(t)] : [],
    chains
  )
  const balance = response[t.id]

  return balance ? balance.toString() : '0'
}
